// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState } from 'react'
import { Box, FormField, Image } from 'grommet'
import { useTranslation } from 'react-i18next'

import {
  Typography,
  Dropdown,
  Ruler,
  Button,
  GLCPHelpMenu
} from '../../../components'
import { isCoP } from '../../../utils/feature-flag-utils'
import VisibilityWrapper from '../../visibility-wrapper/VisibilityWrapper'

import moreResourcesImage from './more-resources.png'
import { HelpMenuList } from './HelpMenuList'

const allAppsList = [
  {
    key: 1,
    label: 'HPE GreenLake Developer Portal',
    value: 'hpe_gl_dev_portal',
    hideFor: {
      account: ['MSP', 'TENANT'],
      deployment: ['COP']
    }
  },
  { key: 2, label: 'HPE GreenLake', value: 'hpe_green_lake' },
  {
    key: 3,
    label: 'HPE GreenLake Central',
    value: 'hpe_green_lake_central',
    hideFor: { account: ['MSP', 'TENANT'], deployment: ['COP'] }
  },
  {
    key: 4,
    label: 'Data Services',
    value: 'data_services',
    hideFor: { account: ['MSP', 'TENANT'], deployment: ['COP'] }
  },
  {
    key: 5,
    label: 'Compute Ops Management',
    value: 'compute_ops_management',
    hideFor: { account: ['MSP', 'TENANT'], deployment: ['COP'] }
  },
  { key: 6, label: 'HPE Aruba Networking Central', value: 'aruba_central' }
]

const feedbackURLs = {
  hpe_gl_dev_portal: {
    general_feedback: '', // Put in the link once gina comes back with it
    feature_request: '' // Same for this one
  },
  hpe_green_lake: {
    general_feedback:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=gl&tof=gf',
    feature_request:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=gl&tof=fr'
  },
  hpe_green_lake_central: {
    general_feedback:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=cs&tof=gf',
    feature_request:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=cs&tof=fr'
  },
  data_services: {
    general_feedback:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=dscc&tof=gf',
    feature_request:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=dscc&tof=fr'
  },
  compute_ops_management: {
    general_feedback:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=cpcc&tof=gf',
    feature_request:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=cpcc&tof=fr'
  },
  aruba_central: {
    general_feedback:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=acc&tof=gf',
    feature_request:
      'https://hpetraining.co1.qualtrics.com/jfe/form/SV_0i8Bsa17cg6MkCy?env=p&pd=acc&tof=fr'
  }
}

const HelpNavContent = () => {
  const { t } = useTranslation(['common', 'support_access_manager'])
  const feedbackTypesList = [
    {
      label: t('help_nav.feedback_dropdown.general_feedback'),
      value: 'general_feedback'
    },
    {
      label: t('help_nav.feedback_dropdown.feature_request'),
      value: 'feature_request'
    }
  ]
  const defaultApp = 'hpe_green_lake'
  const [selectedApp, setSelectedApp] = useState(defaultApp)
  const [selectedFeedbackType, setSelectedFeedbackType] = useState('')
  const handleChangeDrop = (app) => {
    setSelectedApp(app)
  }
  const handleFeedbackTypesChange = (type) => {
    setSelectedFeedbackType(type)
  }
  const handleFeedbackContinue = () => {
    window.open(feedbackURLs[selectedApp][selectedFeedbackType])
  }

  const legalImgUrl = moreResourcesImage

  const helpDrop = {
    hpe_green_lake: <HelpMenuList menuListFor="default" />,
    hpe_green_lake_central: <HelpMenuList menuListFor="HELP_GLCS" />,
    data_services: <HelpMenuList menuListFor="HELP_DS" />,
    compute_ops_management: <HelpMenuList menuListFor="HELP_COM" />,
    aruba_central: <HelpMenuList menuListFor="HELP_AC" />
  }

  const selectDrop = {
    hpe_gl_dev_portal: <HelpMenuList menuListFor="SUPPORT_DEVPORTAL" />,
    hpe_green_lake: <HelpMenuList menuListFor="SUPPORT_GLCP" />,
    hpe_green_lake_central: <HelpMenuList menuListFor="SUPPORT_GLCS" />,
    data_services: <HelpMenuList menuListFor="SUPPORT_DS" />,
    compute_ops_management: <HelpMenuList menuListFor="SUPPORT_COM" />,
    aruba_central: <HelpMenuList menuListFor="SUPPORT_AS" />
  }

  const renderHelpMenu = ({ key, label, value, hideFor }) => {
    return (
      <VisibilityWrapper key={key} hideFor={hideFor}>
        <Box align="start" gap="none" elevation="none" background="none">
          <Box
            background={selectedApp === value ? '#17EBA0' : 'none'}
            width="100%"
            pad="xsmall"
          >
            <Typography
              type="text"
              size="medium"
              emphasis
              color={selectedApp === value ? '#000000' : '#444444'}
            >
              {label}
            </Typography>
          </Box>
        </Box>
      </VisibilityWrapper>
    )
  }

  const HelpMenuData = [
    {
      id: 1,
      label: t('help_nav.help_dropdown.help'),
      content: (
        <Box key={1}>
          <Box pad={{ horizontal: 'medium', top: 'medium' }} gap="small">
            <Typography size="xsmall" type="text">
              {t('help_nav.help_dropdown.help')}
            </Typography>
            <Dropdown
              options={allAppsList}
              defaultVal={selectedApp}
              multiple={false}
              id="help-drop-down"
              testId="help-drop-down"
              placeholder={t('help_nav.dropdown_placeholder')}
              onChangeDropdown={(option) => {
                handleChangeDrop(option)
              }}
              customRender={renderHelpMenu}
            />
          </Box>
          {selectedApp ? (
            <>
              <Box pad={{ horizontal: 'xsmall', top: 'small' }}>
                {helpDrop[selectedApp]}
              </Box>
              <Ruler background="border-weak" />
              <Box pad={{ horizontal: 'small' }} gap="small">
                <Box pad={{ horizontal: 'small' }}>
                  <Typography size="xsmall" type="text">
                    {t('help_nav.help_dropdown.support')}
                  </Typography>
                </Box>
                {selectDrop[selectedApp]}
              </Box>
            </>
          ) : (
            <Box pad={{ horizontal: 'medium' }}>
              <Typography type="paragraph">
                {t('help_nav.help_dropdown.select_description_preselection')}
              </Typography>
            </Box>
          )}
        </Box>
      ),
      isHidden: false
    },
    {
      id: 2,
      label: t('help_nav.feedback_dropdown.feedback'),
      content: (
        <Box key={2} flex>
          <Box pad="medium" gap="xsmall" flex>
            <Typography size="xsmall" type="text" color="text">
              {t('help_nav.feedback_dropdown.give_feedback')}
            </Typography>
            <FormField
              htmlFor="feedback-drop-down"
              label={t('help_nav.feedback_dropdown.which_product')}
            >
              <Dropdown
                options={allAppsList}
                defaultVal={selectedApp}
                multiple={false}
                id="feedback-drop-down"
                testId="feedback-drop-down"
                placeholder={t('help_nav.dropdown_placeholder')}
                onChangeDropdown={(option) => handleChangeDrop(option)}
                customRender={renderHelpMenu}
                plain
              />
            </FormField>
            <FormField
              htmlFor="feedback-types-drop-down"
              label={t('help_nav.feedback_dropdown.what_feedback')}
            >
              <Dropdown
                options={feedbackTypesList}
                defaultVal={selectedFeedbackType}
                multiple={false}
                id="feedback-types-drop-down"
                testId="feedback-types-drop-down"
                placeholder={t('help_nav.dropdown_placeholder')}
                onChangeDropdown={(option) => handleFeedbackTypesChange(option)}
                plain
              />
            </FormField>
          </Box>
          <Box
            border={{ side: 'top', color: 'border-weak' }}
            pad={{ horizontal: 'medium', vertical: 'small' }}
          >
            <Button
              primary
              label={t('continue')}
              disabled={!selectedApp || !selectedFeedbackType}
              onClick={handleFeedbackContinue}
              testId="feedback-continue-btn"
            />
          </Box>
        </Box>
      ),
      isHidden: isCoP()
    },
    {
      id: 3,
      label: t('help_nav.legal_dropdown.legal'),
      content: (
        <Box key={3}>
          <VisibilityWrapper hideFor={{ deployment: 'COP' }}>
            <Box
              pad={{ top: 'small', bottom: 'medium', horizontal: 'medium' }}
              border={{ side: 'bottom', color: 'border-weak' }}
              gap="small"
            >
              <Box
                align="center"
                justify="center"
                overflow="hidden"
                round="xsmall"
              >
                <Image
                  src={legalImgUrl}
                  alt="Two information technology professionals having a discussion with HPE server racks in the background."
                  fit="cover"
                />
              </Box>

              <Typography type="text" emphasis>
                {t('help_nav.legal_dropdown.learn_more')}
              </Typography>
              <Typography size="small" type="text" background="text-light">
                {t('help_nav.legal_dropdown.desc')}
              </Typography>
              <Button
                secondary
                label={t('help_nav.legal_dropdown.subscribe_now')}
                onClick={() =>
                  window.open(
                    'https://www.hpe.com/us/en/newsroom/subscribe.html',
                    '',
                    'noopener'
                  )
                }
                testId="subscribe-now-btn"
              />
            </Box>
          </VisibilityWrapper>
          <Box pad={{ horizontal: 'small', vertical: 'medium' }} gap="small">
            <Box pad={{ horizontal: 'small' }}>
              <Typography size="xsmall" type="text">
                {t('help_nav.legal_dropdown.resources')}
              </Typography>
            </Box>
            <HelpMenuList menuListFor="LEGAL_RESOURCES" />
          </Box>
        </Box>
      ),
      isHidden: false
    }
  ]
  return <GLCPHelpMenu helpNavContent={HelpMenuData} testId="help-menu" />
}

export { HelpNavContent }
